import type { Branch } from "@domain/CRM/branch";
import { planBranchLoadScope } from "@domain/CRM/branch";
import type { Company } from "@domain/CRM/company";
import type { Job } from "@domain/CRM/job";
import type { IBranches } from "@ports/branches/IBranches";
import type { IJobs } from "@ports/jobs/IJobs";
import type { IJobIssues } from "@ports/jobs/IJobIssues";

export interface BranchMatcherUseCasesDeps {
	jobIssuesApi: IJobIssues;
	jobsApi: IJobs;
	branchesApi: IBranches;
}

/**
 * The result of `loadIssues` — the unmatched jobs plus the candidate
 * branches across the relevant companies, sorted by branch name. The
 * scope-narrowing step ("which companies' branches do we even need to
 * load?") delegates to the `planBranchLoadScope` domain operation so
 * it stays unit-testable in isolation.
 */
export interface BranchMatcherLoadResult {
	jobs: Job[];
	branches: Branch[];
}

/**
 * Drives the Admin → Branch Matcher page IO layer:
 *
 *  - `loadIssues(companies)` — fetch the unmatched jobs, derive the set
 *    of companies whose branches we need (per `planBranchLoadScope`),
 *    fetch all those branches in a single batched POST, and return both
 *    lists. Branches arrive sorted by name; jobs in server order.
 *
 *  - `applyAssignments(map)` — apply each pending `jobId → branchId`
 *    pair via a targeted single-field update (`branchIds`). The matcher
 *    page can't `updateMany` because the lite payload it loaded doesn't
 *    include unrelated fields like `dealIds` or `salespersonId` —
 *    sending a full Job back would clobber those.
 *
 * The vm keeps drag-drop state, search/sort/filter state, the
 * "pendingAssignments" map, and the StrictMode-dedup ref.
 */
export interface BranchMatcherUseCases {
	loadIssues(companies: Company[]): Promise<BranchMatcherLoadResult>;
	/** @param assignments Map of `jobId → branchId`. */
	applyAssignments(assignments: Map<string, string>): Promise<void>;
}

export async function loadIssues(
	deps: BranchMatcherUseCasesDeps,
	companies: Company[],
): Promise<BranchMatcherLoadResult> {
	const jobs = await deps.jobIssuesApi.getBranchMatcherJobs();
	const companiesToLoad = planBranchLoadScope(jobs, companies);
	const allBranchIds = companiesToLoad.flatMap((c) => c.branchIds || []);
	if (allBranchIds.length === 0) {
		return { jobs, branches: [] };
	}
	const fetched = await deps.branchesApi.getByIds(allBranchIds);
	const branches = fetched.slice().sort((a, b) => a.name.localeCompare(b.name));
	return { jobs, branches };
}

export async function applyAssignments(
	deps: BranchMatcherUseCasesDeps,
	assignments: Map<string, string>,
): Promise<void> {
	const entries = [...assignments.entries()];
	await Promise.all(entries.map(([jobId, branchId]) => deps.jobsApi.updateField(jobId, "branchIds", [branchId])));
}

export function makeBranchMatcherUseCases(deps: BranchMatcherUseCasesDeps): BranchMatcherUseCases {
	return {
		loadIssues: (companies) => loadIssues(deps, companies),
		applyAssignments: (assignments) => applyAssignments(deps, assignments),
	};
}
